import { type CSSProperties, useId } from "react";

import type { RegisteredComponent } from "@builder.io/sdk-react";

import { TelehealthGetStartedButton } from "~/hec/telehealth/components/get-started-button";
import { PDP_HOW_IT_WORKS_CTA_CONTEXT } from "~/hec/telehealth/cta-contexts";
import { CircleCheckOutline } from "~/hec/telehealth/icons";
import { TREATMENT_SLUGS } from "~/hec/telehealth/treatments";
import { cn } from "~/lib/ui";

/** Fallback heading. Also the image's fallback accessible name — see `altFor`. */
const DEFAULT_HEADING = "How it Works";

export interface TelehealthHowItWorksStep {
  text?: string;
}

export interface TelehealthHowItWorksProps {
  heading?: string;
  subheading?: string;
  steps?: TelehealthHowItWorksStep[];
  image?: string;
  imageAlt?: string;
  imageDecorative?: boolean;
  imagePosition?: "left" | "right";
  imageAspectRatio?: string;
  ctaLabel?: string;
  treatment?: string;
  /**
   * Section background. No default — an unset value emits no inline background
   * at all, which is what lets Builder's Style tab take effect. See the merge
   * in the component body.
   */
  backgroundColor?: string;
  /** Builder injects this on `noWrap` components — must spread on the root. */
  attributes?: Record<string, unknown>;
}

/**
 * Resolve the image's accessible name.
 *
 * `required: true` on a Builder input is editor-side advisory — it does not
 * block publishing, and the field is hidden entirely while `imageDecorative` is
 * on. So "author never filled it in" and "author decided it's decorative" both
 * arrive here as an absent value, and defaulting both to `alt=""` would be the
 * empty-string guesswork this block exists to remove: silent, and green in axe.
 *
 * Only the explicit toggle earns `alt=""`. Otherwise fall back to the section
 * heading — an imperfect name, but a real one, and one that shows up in an audit
 * as "review this" rather than passing as deliberate. Whitespace-only alt text
 * is treated as absent (`alt="  "` is a non-empty name that readers commonly
 * replace with the image URL).
 */
function altFor({
  imageDecorative,
  imageAlt,
  heading,
}: {
  imageDecorative: boolean;
  imageAlt?: string;
  heading: string;
}): string {
  if (imageDecorative) return "";
  return imageAlt?.trim() || heading;
}

/**
 * ET-216 — the treatment PDP's below-the-fold "How it Works" section, authored
 * in the `pdp-glp-1-pricing` entry's `belowFold` region (XD-149).
 *
 * Purpose-built rather than composed from `HeroTwoColumn` for three reasons,
 * all accessibility:
 *
 *  - `HeroTwoColumn` has no alt-text input at all, so its image can never carry
 *    an accessible name. Here alt text is a first-class field, with an explicit
 *    decorative toggle instead of the empty-string guesswork (see `altFor`).
 *  - The steps render as a real `<ul>`/`<li>`, so a screen reader announces
 *    "list, 4 items". A rich-text bullet list in a hero's text blob doesn't
 *    survive as reliably, and can't carry the circled-check glyph.
 *  - The CTA is the shared {@link TelehealthGetStartedButton}, NOT an authorable
 *    link. A hand-entered URL would skip the ET-12 availability gate and
 *    attribute intake starts differently from the buy column's identical
 *    button — two "Get Started"s on one page reporting two ways.
 *
 * `noWrap` + spread `attributes` per the Builder convention (CLAUDE.md rule 4).
 */
export function TelehealthHowItWorks({
  heading,
  subheading,
  steps,
  image,
  imageAlt,
  imageDecorative = false,
  imagePosition = "right",
  imageAspectRatio = "4 / 3",
  ctaLabel = "Get Started",
  treatment = "weight-loss",
  backgroundColor,
  attributes,
}: TelehealthHowItWorksProps) {
  // A cleared Builder field publishes `""`, which a parameter default does NOT
  // catch (it only fires on `undefined`). Without this the `<h2>` disappears
  // and the buy column's anchor link lands on an unnamed region.
  const sectionHeading = heading?.trim() || DEFAULT_HEADING;

  // Per-instance, not a module constant: the block is registered globally, so
  // it can appear twice on one page (e.g. in `belowFold` AND the canvas of the
  // same entry). Two identical ids would make the second section's
  // `aria-labelledby` resolve to the FIRST heading. `useId` is SSR-stable.
  const headingId = useId();

  // Builder hands back list rows for every added item, including ones the
  // author left blank — those would render as empty bullets.
  const items = (steps ?? [])
    .map(step => step?.text?.trim())
    .filter((text): text is string => Boolean(text));

  // Desktop column assignment. Written as whole class names (not interpolated
  // fragments) so Tailwind's scanner can see them.
  const desktopTextColumn =
    imagePosition === "left"
      ? "min-[768px]:col-start-2"
      : "min-[768px]:col-start-1";
  const desktopImageColumn =
    imagePosition === "left"
      ? "min-[768px]:col-start-1"
      : "min-[768px]:col-start-2";

  // Merge rather than replace: `attributes` can legitimately carry a `style`
  // from the editor, and clobbering it would drop author-set positioning.
  //
  // NO background is emitted unless an author picked one, and that absence is
  // load-bearing. Builder's Style tab writes `block.responsiveStyles`, which
  // the SDK renders as a scoped CLASS rule, so any inline `background-color`
  // here outranks it. This block used to default to `#F4F4F4`, which made the
  // inline value effectively unconditional — the section was always grey and a
  // background chosen in the Style tab silently did nothing. Two review rounds
  // fixed the merge and the default agreement respectively and cancelled each
  // other out, because neither removed the default. There is no default now:
  // the input styles the section when set, the Style tab works when it isn't,
  // and an unstyled section is transparent.
  const rootStyle: CSSProperties = {
    ...(attributes?.style as CSSProperties | undefined),
    ...(backgroundColor ? { backgroundColor } : {}),
  };

  return (
    <section
      {...attributes}
      style={rootStyle}
      // A bare <section> has no accessible name and is therefore not exposed as
      // a landmark at all. The buy column's "How it Works" link jumps to the
      // WRAPPER around all below-fold sections (`HOW_IT_WORKS_ANCHOR_ID`), not
      // here — so naming this region is what tells a screen-reader user which
      // of the stacked sections they landed in.
      aria-labelledby={headingId}
      data-testid="pdp-how-it-works-section"
    >
      {/* Three grid items — copy, image, CTA — rather than a text column and
          an image column. DOM order IS the mobile order (copy → image → CTA),
          which is what the design asks for: the vials sit between the checklist
          and the Get Started button when stacked. Desktop can't just reflow
          that, because there the CTA belongs under the checklist in the left
          column — so the image is placed explicitly, spanning both rows of
          column 2, and copy/CTA take rows 1 and 2 of column 1.

          `gap-x-0`: the columns butt directly against each other — the image
          carries its own whitespace in the asset, so a grid gap would read as
          a double margin. `gap-y-6` is the row gap: between stacked items on
          mobile, and between the checklist and the CTA on desktop (which is
          why the CTA carries no top margin of its own). */}
      <div
        className={cn(
          "min-[768px]:px-6 min-[768px]:py-9 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-x-0 gap-y-6 px-4 py-6",
          image && "min-[768px]:grid-cols-2",
        )}
      >
        <div
          className={cn(
            "flex flex-col items-start",
            image && desktopTextColumn,
            image && "min-[768px]:row-start-1",
          )}
        >
          <h2
            id={headingId}
            className="font-grotesque text-3xl font-extrabold leading-tight text-deep-blue"
          >
            {sectionHeading}
          </h2>

          {subheading ? (
            <p className="mt-2 max-w-md font-grotesque text-base font-normal leading-snug text-deep-blue">
              {subheading}
            </p>
          ) : null}

          {items.length ? (
            <ul
              className="mt-5 flex flex-col gap-4"
              data-testid="pdp-how-it-works-steps"
            >
              {items.map((text, i) => (
                // Index-prefixed so two steps with identical text don't produce
                // duplicate keys. Index-dominant on purpose: these <li>s hold no
                // state, so reorder identity doesn't matter — uniqueness does.
                <li key={`${i}-${text}`} className="flex items-start gap-3">
                  {/* Explicit 24px: this repo's Tailwind scale is non-stock,
                      where `h-6` resolves to 32px. `mt-[0.15rem]` optically
                      centres the glyph against the first line of a two-line
                      step rather than the block. */}
                  <CircleCheckOutline className="mt-[0.15rem] h-[1.5rem] w-[1.5rem] shrink-0 text-deep-blue" />
                  <span className="max-w-sm font-grotesque text-base font-normal leading-snug text-deep-blue">
                    {text}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {image ? (
          <div
            className={cn(
              "flex w-full justify-center",
              desktopImageColumn,
              "min-[768px]:row-span-2 min-[768px]:row-start-1",
            )}
            // Reserve the box before the lazy image decodes. Builder's `file`
            // input yields a URL with no dimensions, so intrinsic width/height
            // aren't available — without a reserved ratio the image resizes its
            // grid row on decode and shifts the copy column and the CTA. CLS is
            // the deterministic metric in the PR perf budgets
            // (docs/perf-budgets-ci.md), so this shows up there.
            style={{ aspectRatio: imageAspectRatio }}
          >
            <img
              src={image}
              alt={altFor({
                imageDecorative,
                imageAlt,
                heading: sectionHeading,
              })}
              loading="lazy"
              decoding="async"
              className="h-full w-full max-w-xl object-contain"
              data-testid="pdp-how-it-works-image"
            />
          </div>
        ) : null}

        {/* Renders nothing for an unknown/unavailable treatment — the same
            fail-closed rule the buy column's CTA follows. Last in the DOM so
            the image precedes it when stacked; pinned back under the copy on
            desktop via row 2 of the text column. Width classes come from the
            primitive — only grid placement is passed here. */}
        <TelehealthGetStartedButton
          treatment={treatment}
          source={PDP_HOW_IT_WORKS_CTA_CONTEXT.source}
          ctaLocation={PDP_HOW_IT_WORKS_CTA_CONTEXT.ctaLocation}
          label={ctaLabel}
          className={cn(
            image && desktopTextColumn,
            image && "min-[768px]:row-start-2",
          )}
          data-testid="pdp-how-it-works-get-started-button"
        />
      </div>
    </section>
  );
}

export const TelehealthHowItWorksDefinition = {
  component: TelehealthHowItWorks,
  name: "TelehealthHowItWorks",
  noWrap: true,
  inputs: [
    {
      name: "heading",
      type: "string",
      defaultValue: DEFAULT_HEADING,
    },
    {
      name: "subheading",
      type: "string",
      friendlyName: "Subheading",
      helperText: "Optional line under the heading.",
    },
    {
      name: "steps",
      type: "list",
      friendlyName: "Steps",
      helperText:
        "The checklist items, in order. Each renders with a circled checkmark.",
      subFields: [
        {
          name: "text",
          type: "string",
          required: true,
        },
      ],
      // Deliberately a single neutral placeholder rather than the four steps
      // from the design. A `defaultValue` is baked into the code and is
      // site-agnostic, so shipping the real copy here would (a) put an
      // FSA-card-specific line on HSASTORE and WDH, (b) put a regulated claim
      // about provider review somewhere Legal can't revise it, and (c) make
      // changing any of it a deploy. The real copy belongs in the entry.
      defaultValue: [{ text: "Answer a few quick questions." }],
    },
    {
      name: "image",
      type: "file",
      friendlyName: "Image",
      allowedFileTypes: ["jpeg", "jpg", "png", "webp", "svg"],
      helperText: "Image for the opposite column.",
    },
    {
      name: "imageDecorative",
      type: "boolean",
      friendlyName: "Image is decorative",
      defaultValue: false,
      helperText:
        "Turn on if the image adds nothing beyond the text on the left — it will be hidden from screen readers.",
    },
    {
      name: "imageAlt",
      type: "string",
      friendlyName: "Image alt text",
      required: true,
      showIf: `options.get('imageDecorative') !== true`,
      helperText:
        'Describe the image for screen readers, e.g. "Compounded Tirzepatide and Semaglutide vials". Do not start with "image of". Left blank, the section heading is used instead.',
    },
    {
      name: "imageAspectRatio",
      type: "string",
      friendlyName: "Image aspect ratio",
      defaultValue: "4 / 3",
      helperText:
        'Reserves space before the image loads, so the page does not jump. Match your asset, e.g. "4 / 3" or "1 / 1".',
    },
    {
      name: "imagePosition",
      type: "string",
      friendlyName: "Image position (desktop)",
      enum: ["right", "left"],
      defaultValue: "right",
      helperText: "Which side the image sits on at desktop.",
    },
    {
      name: "ctaLabel",
      type: "string",
      friendlyName: "CTA label",
      defaultValue: "Get Started",
    },
    {
      name: "treatment",
      type: "string",
      friendlyName: "Treatment",
      enum: [...TREATMENT_SLUGS],
      defaultValue: "weight-loss",
      helperText:
        "Treatment the Get Started button starts intake for. The button hides itself if the treatment isn't available.",
    },
    {
      name: "backgroundColor",
      type: "color",
      friendlyName: "Background colour",
      helperText:
        "Section background. Leave empty for no background — you can also set one from the Style tab.",
    },
  ],
} satisfies RegisteredComponent;
